import {
  setISOWeek,
  startOfISOWeek,
  endOfISOWeek,
  subDays,
  format,
  addBusinessDays,
} from 'date-fns';

/** Orders ship from Denmark, so the dispatch clock is Danish regardless of which store is being viewed. */
const SHIPPING_TIME_ZONE = 'Europe/Copenhagen';

function getFirstAndLastDayOfWeek(weekNumber, year) {
  year = year || new Date().getFullYear();
  const start = new Date(year, 0, 1);
  const week = setISOWeek(start, weekNumber);
  return {
    first: startOfISOWeek(week),
    last: subDays(endOfISOWeek(week), 2),
  };
}

function getNumberOfWeek(numOfWeeks) {
  let now = new Date();
  now.setDate(now.getDate() + numOfWeeks * 7);
  const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
  const pastDaysOfYear = (now - firstDayOfYear) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}

function getEstimatedDeliverySpan(extraWeeks, dateFormat = 'MM/dd') {
  const { first, last } = getFirstAndLastDayOfWeek(getNumberOfWeek(extraWeeks));
  const firstFormatted = format(first, dateFormat);
  const lastFormatted = format(last, dateFormat);
  return `${firstFormatted} - ${lastFormatted}`;
}

const getMtoDeliveryDate = (deliveryTime, string, weekString, region) => {
  // Normalize and coerce inputs
  const regionCode = (region || '').toString().toUpperCase();
  const weeksOffset = Number(deliveryTime) / 7;

  // DK uses explicit week number phrasing
  if (regionCode === 'DK') {
    const numberOfWeek = getNumberOfWeek(weeksOffset);
    return `${string}: ${weekString} ${numberOfWeek}`;
  }

  // Select date format: US => MM/dd, everywhere else => dd/MM
  const dateFormat = regionCode === 'US' ? 'MM/dd' : 'dd/MM';
  const estimatedDeliverySpan = getEstimatedDeliverySpan(weeksOffset, dateFormat);

  return `${string}: ${estimatedDeliverySpan}`;
};

/**
 * Today's calendar day at the warehouse, as a floating local Date.
 * The shopper's own timezone must not shift the window, so the date parts are read in the
 * warehouse zone and rebuilt locally — every subsequent operation is calendar-only.
 * @param {string} [timeZone] IANA zone to read the date in.
 * @returns {Date} Local Date carrying the warehouse's Y/M/D.
 */
function getShippingToday(timeZone = SHIPPING_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(new Date())
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

  return new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
}

/**
 * Expected delivery window for in-stock products, counted in business days from the warehouse's
 * today. Nothing dispatches the same day, so the floor of 1 keeps the earliest date on the next
 * business day whatever the merchant types into the day range. Weekends are excluded — a Friday or
 * weekend visitor counts from Monday — and public holidays are not.
 * @param {number} minDays Earliest delivery, in business days from today.
 * @param {number} maxDays Latest delivery, in business days from today.
 * @param {string} [dateFormat] date-fns format string.
 * @returns {string} Formatted range, e.g. `11/08-14/08`.
 */
const getDeliveryDateRange = (minDays, maxDays, dateFormat = 'dd/MM') => {
  const today = getShippingToday();
  const minDate = addBusinessDays(today, Math.max(1, minDays));
  const maxDate = addBusinessDays(today, Math.max(1, maxDays));
  return `${format(minDate, dateFormat)}-${format(maxDate, dateFormat)}`;
};

export { getMtoDeliveryDate, getDeliveryDateRange };
